/**
 * What a page hosting the composition editor does around it: load a quantity composition into the
 * builder, check a composition before a save, and serialise it for the save request. Manual Edit and
 * Manual Creation both host the editor in quantity mode, so both import these.
 */
import type { InvestmentSummary, ReviewTicker } from "@eu.mdotm.api/sphere";
import { formatDate } from "$root/localization/formatters";
import { platformToast } from "$root/notification-system/toast";
import { isInstrumentCountValid } from "$root/pages/Portfolios/common";
import { labSum } from "$root/utils/monetary-math";
import { t } from "i18next";
import { OrderedMap } from "immutable";
import type { RefObject } from "react";
import type { EditorBannersHandle } from "../banners/EditorBanners";
import { covertInstrumentEditorToReviewTicker } from "../builder";
import type { Instrument } from "../const";
import { weightTotalStatus } from "../weight-total";
import type { QuantityEditorBuilderProps } from "./builder";
import { markHeldByValue } from "./heldByValue";
import { dropUnfixable } from "./new-instruments/checks";
import { convertQuantityCompositionToReviewTicker } from "./serialize";

/**
 * Pulses the editor banner that blocks the save, and says in a toast that the save waits on it.
 * Answers whether there was one.
 */
export function saveBlockedByBanner(editorBanners: RefObject<EditorBannersHandle | null>): boolean {
	const banner = editorBanners.current?.revealBlocking();
	if (banner == null) {
		return false;
	}
	platformToast({
		children: t("EDIT_COMPOSITION.SAVE_BLOCKED", { title: banner.title }),
		severity: "warning",
		icon: "Portfolio",
	});
	return true;
}

/**
 * The checks a composition has to pass before either save path submits it: no banner in the editor
 * that blocks the save, enough instruments, every row priced at the reference date, every added
 * instrument checked and fixed, and a valid total. Raises the toast that says which one failed
 * (pulsing the blocking banner too) and answers `false`, so a caller only has to return.
 *
 * The 100 % weight total is a traditional-composition rule only: in quantity mode (MDOT-24700)
 * the composition is a fixed AUM spent on whole shares, so the weights carry rounding
 * remainders and the cash row is what has to stay non-negative (invariant I4).
 */
export function compositionPassesSubmitChecks(
	instrumentBuilder: QuantityEditorBuilderProps,
	nOfInstrumentsInComposition: number,
	bounds: { minInstrumentsInComposition: number; maxInstrumentsInComposition: number; weightTotalPrecision: number },
	editorBanners: RefObject<EditorBannersHandle | null>,
): boolean {
	const { minInstrumentsInComposition, maxInstrumentsInComposition, weightTotalPrecision } = bounds;

	if (saveBlockedByBanner(editorBanners)) {
		return false;
	}

	if (nOfInstrumentsInComposition < minInstrumentsInComposition) {
		platformToast({
			children: `You must add at least ${minInstrumentsInComposition} instruments to your composition before submitting. Please include more instruments to proceed.`,
			severity: "warning",
			icon: "Portfolio",
		});
		return false;
	}

	if (instrumentBuilder.hasUnpricedRows()) {
		platformToast({
			children: t("EDIT_COMPOSITION.REFERENCE_DATE.UNPRICED_SUBMIT_BLOCKED", {
				date: formatDate(instrumentBuilder.getMarketDate().value),
			}),
			severity: "warning",
			icon: "Portfolio",
		});
		return false;
	}

	if (instrumentBuilder.hasRowsNeedingFix()) {
		platformToast({
			children: t("EDIT_COMPOSITION.NEW_INSTRUMENTS.SUBMIT_BLOCKED"),
			severity: "warning",
			icon: "Portfolio",
		});
		return false;
	}

	const instrumentCountValid = isInstrumentCountValid(nOfInstrumentsInComposition, {
		maxInstrumentsInComposition,
		minInstrumentsInComposition,
	});
	const canSubmit = instrumentBuilder.isQuantityMode()
		? instrumentCountValid
		: weightTotalStatus(instrumentBuilder.getTotal("weight"), weightTotalPrecision) === "exact" && instrumentCountValid;

	if (!canSubmit) {
		platformToast({
			children: `Unable to generate your Portfolio please review your composition`,
			severity: "warning",
			icon: "Portfolio",
		});
		return false;
	}

	return true;
}

// Submit serialization (MDOT-24700): in quantity mode every row carries its
// quantity/price/marketValue (cash included, market value only); otherwise the
// weight-only mapper is unchanged. The AUM the rows were sized against is not part of the
// composition: every save path sends it alongside, on the system-managed AUM custom attribute
// of the entity it saves to (`resolveCustomAttributesWithAum`), so budget and composition land
// in one request. `getAum()` is `null` in weight mode, which leaves the attributes untouched.
export function serializeComposition(instrumentBuilder: QuantityEditorBuilderProps): ReviewTicker[] {
	const rows = Array.from(instrumentBuilder.getComposition({ removeDeleted: true }).values());
	// An added instrument that cannot be held is left out of the save.
	return instrumentBuilder.isQuantityMode()
		? convertQuantityCompositionToReviewTicker(dropUnfixable(rows))
		: covertInstrumentEditorToReviewTicker(rows);
}

/**
 * Initial quantity-mode load (MDOT-24700): the backend rows already carry
 * quantity/price/marketValue (mapped by createInstrumentList). The cash position arrives as
 * a composition row with market value only — flag it `isCash` (matched by the response's
 * cash ticker) so the edit engine rebalances it instead of injecting its own. A row with no
 * quantity is held by value (`instruments-editor/quantity/heldByValue`), so it is sized in money from the start.
 *
 * The AUM budget is the portfolio's own AUM, held on the system-managed AUM custom attribute
 * in main info and passed in as `aum` — a value the user sets, independent of what the
 * composition currently adds up to. `null` (the tenant exposes no AUM attribute, or this
 * portfolio carries no value on it) falls back to Σ market value with cash included, which
 * is the composition's own total and keeps the editor usable where no AUM is set.
 */
export function resetQuantityComposition(
	instrumentBuilder: QuantityEditorBuilderProps,
	params: {
		instruments: Instrument[];
		cash: Instrument | undefined;
		/** Ticker identifying the cash row among `instruments` (the response's `cashTicker.ticker`). */
		cashTickerName: string | undefined;
		/** Main-info AUM of the entity being edited; `null` when it carries none. */
		aum: number | null;
		currency: InvestmentSummary["baseCurrency"];
		/** Bounds the reference date the editor's header offers (MDOT-25190). */
		lastTradeDate: InvestmentSummary["lastTradeDate"];
		/**
		 * The entity being edited — what the market date's price call is addressed to (MDOT-25191).
		 * Absent while the composition belongs to no entity yet.
		 */
		investmentUuid?: string;
	},
): void {
	const { instruments, cash, cashTickerName, currency, lastTradeDate, investmentUuid } = params;
	const rows = markHeldByValue(
		instruments.map((instrument) =>
			cashTickerName != null && instrument.tickerName === cashTickerName ? { ...instrument, isCash: true } : instrument,
		),
	);
	const aum = params.aum ?? labSum(rows, (instrument) => instrument.marketValue ?? 0);
	instrumentBuilder.reset(
		{
			composition: OrderedMap<string, Instrument>(rows.map((x) => [x.id, x])),
			cash,
			lastTradeDate,
			quantity: { aum, currency, investmentUuid },
		},
		{ disableToggleDirty: true },
	);
}
